import { useState } from 'react';

import { NumberField, PropRow, SelectField, Toggle } from '../ui/controls.js';
import type { UvOpDef, UvParamValue } from './uv-ops.js';

/** Options of a UV operator before it runs (Blender's operator popup): OK runs, Escape / Cancel closes. */
export function UvOpDialog({
  def,
  initial,
  onRun,
  onClose,
}: {
  def: UvOpDef;
  initial: Record<string, UvParamValue>;
  onRun: (params: Record<string, UvParamValue>) => void;
  onClose: () => void;
}) {
  const [values, setValues] = useState(initial);
  const set = (key: string, value: UvParamValue) => setValues((v) => ({ ...v, [key]: value }));
  return (
    <div className="uv-dialog-backdrop" onMouseDown={onClose}>
      <form
        className="uv-dialog"
        role="dialog"
        aria-label={def.label}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
        onSubmit={(e) => {
          e.preventDefault();
          onRun(values);
        }}
      >
        <div className="uv-dialog-title">{def.label}</div>
        {(def.fields ?? []).map((f) => (
          <PropRow key={f.key} label={f.label} hint={f.hint}>
            {f.kind === 'bool' ? (
              <Toggle checked={values[f.key] === true} onChange={(v) => set(f.key, v)} />
            ) : f.kind === 'select' ? (
              <SelectField value={String(values[f.key] ?? f.default)} options={f.options ?? []} onChange={(v) => set(f.key, v)} />
            ) : (
              <NumberField
                value={Number(values[f.key] ?? f.default)}
                min={f.min}
                max={f.max}
                step={f.step}
                integer={f.kind === 'int'}
                onChange={(v) => set(f.key, v)}
              />
            )}
          </PropRow>
        ))}
        <div className="uv-dialog-actions">
          <button type="button" className="tb-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="tb-btn is-primary" autoFocus>
            OK
          </button>
        </div>
      </form>
    </div>
  );
}
